import { Component, Input, OnInit } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { AssignmentActivity, AssignmentActivityCategory, AssignmentActivityUnit } from '../data/assignment-activities';
import { AssignedActivityDetail, AssignmentDetails } from '../data/assignment-details';
import { Class } from '../data/class';
import { FormatUtilities } from '../utilities/format-utilities';
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import swal from 'sweetalert2/dist/sweetalert2.js';
import Swal from 'sweetalert2';
import { Student } from '../data/student';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-class-assignment-details',
  templateUrl: './class-assignment-details.component.html',
  styleUrls: ['./class-assignment-details.component.scss']
})
export class ClassAssignmentDetailsComponent extends BaseComponent implements OnInit {
  longDateFormat = FormatUtilities.longDateFormatUtc;
  assignment: AssignmentDetails;
  assignmentState: string;
  totalMinTime: number;
  totalMaxTime: number;
  organizationId: number;
  class: Class;
  activityCategories: AssignmentActivityCategory[];
  activityLists: AssignmentActivity[][] = [];
  unitLists: any = {};
  activitySummary: { ImageName: string, Summary: string, SummarizedActivity: AssignedActivityDetail };
  loading: { class, assignment, categories, activityLists, unitLists } 
    = { class: true, assignment: true, categories: true, activityLists: [], unitLists: {} };
  changed: boolean;
  validator: { category, activity, unit }[] = [];  
  uneditableAssignedActivities: AssignedActivityDetail[] = [];
  editableAssignedActivities: AssignedActivityDetail[] = [];
  editingHeader: boolean = false;
  oldHeaderValues: { Name: string, StartDate: string, EndDate: string };
  students: Student[];

  kadyChainID: string = "d4f790d8-7d4f-4bee-b85f-c60cf1553c24";

  private readonly POST_TEST_FULL = "Post Test Full Test ca7267ca-48f4-4e6b-933c-4e76969ab9ba";
  private readonly POST_TEST_HALF = "Post Test First Half ca7267ca-48f4-4e6b-933c-4e76969ab9ba";

  ngOnInit() {
    this.changed = false;
    this.route.params.subscribe(params => {
      this.organizationId = params.organizationId;
      this.dataService.getClass(params.classId).subscribe(classInfo => {
        this.class = classInfo;
        this.loading.class = false;
      });
      this.dataService.getClassAssignmentDetails(this.organizationId, params.assignmentId).subscribe(assignmentDetails => {
        this.assignment = assignmentDetails;
        if (!this.assignment.AssignedStudents)
          this.assignment.AssignedStudents = [];
        
        if (this.isPostTestAssignment()) {
          this.uneditableAssignedActivities = this.assignment.ActivitySequence.Activities;
          this.editableAssignedActivities = [];
        } else {
          this.assignmentState = this.getStateForAssignment();
          if(!this.assignment.ActivitySequence.Activities) {
            this.assignment.ActivitySequence.Activities = [];
          }

          this.fixKadyChain();

          const preloadCategories = new Set(this.assignment.ActivitySequence.Activities.map(activity => activity.CategoryID));
          for (let category of preloadCategories) this.loadActivitiesForCategory(category);

          const preloadActivities = new Set(this.assignment.ActivitySequence.Activities.map(activity => activity.ActivityID));
          for (let activity of preloadActivities) this.loadUnitsForActivity(activity);

          if (this.assignmentState === "upcoming") {
            this.editableAssignedActivities = this.assignment.ActivitySequence.Activities;
            this.ensureEmptyRow();
          }
          else if (this.assignmentState === "in-progress") {
            this.uneditableAssignedActivities = this.assignment.ActivitySequence.Activities;
            this.editableAssignedActivities = [];
            this.ensureEmptyRow();
          }
          else {
            this.uneditableAssignedActivities = this.assignment.ActivitySequence.Activities;
          }
        }
        
        this.calculateTotalTimes();
        this.loading.assignment = false;
      });
      this.dataService.getStudents(true, params.classId).subscribe(s => {
        this.students = s;
      });
    });
    this.dataService.getActivityCategories().subscribe(categories => {
      this.activityCategories = categories;
      this.loading.categories = false;
    })
  }

  private isPostTestAssignment(): boolean {
    return this.assignment?.ActivitySequence?.Name === this.POST_TEST_FULL || 
           this.assignment?.ActivitySequence?.Name === this.POST_TEST_HALF;
  }

  getStateForAssignment() {
    if (this.isPostTestAssignment()) {
      return "completed";
    }

    let today = new Date();
    let startDateParts = this.assignment.StartDate.split('-').map(string => Number(string));
    let assignmentStart = new Date(startDateParts[0], startDateParts[1]-1, startDateParts[2]);
    if (assignmentStart > today) return "upcoming";

    let endDateParts = this.assignment.EndDate.split('-').map(string => Number(string));
    let assignmentEnd = new Date(endDateParts[0], endDateParts[1]-1, endDateParts[2]);
    assignmentEnd.setDate(assignmentEnd.getDate() + 1); // assignments last until the END of EndDate
    if (assignmentEnd < today) return "completed";

    return "in-progress";
  }

  getIconForState(state: string) {
    if(state === "upcoming") return "fas fa-circle upcoming";
    if(state === "completed") return "fas fa-square completed";
    if(state === "in-progress") return "fas fa-star in-progress";
    return "fas fa-question-mark";
  }

  calculateTotalTimes() {
    let minSum = 0;
    let maxSum = 0;

    if (this.uneditableAssignedActivities) {
      for (const activity of this.uneditableAssignedActivities) {
        minSum += activity.MinTime || 0;
        maxSum += activity.MaxTime || 0;
      }
    }

    if (this.editableAssignedActivities) {
      for (const activity of this.editableAssignedActivities) {
        minSum += activity.MinTime || 0;
        maxSum += activity.MaxTime || 0;
      }
    }

    this.totalMinTime = minSum;
    this.totalMaxTime = maxSum;
  }

  categoryChanged(activityIndex: number) {
    this.clearCategoryError(activityIndex);
    this.changed = true;
    const selectedActivity = this.editableAssignedActivities[activityIndex];
    selectedActivity.ActivityID = undefined;
    selectedActivity.UnitNumber = undefined;
    this.clearActivityRowTime(activityIndex);
    if (!selectedActivity.CategoryID) return;

    this.loadActivitiesForCategory(selectedActivity.CategoryID);
  }

  activityChanged(activityIndex: number) {
    this.clearActivityError(activityIndex);
    this.changed = true;
    const changedActivityAssignment = this.editableAssignedActivities[activityIndex];
    changedActivityAssignment.UnitNumber = undefined;
    if (!changedActivityAssignment.ActivityID) return;

    const selectedActivity = this.activityLists[changedActivityAssignment.CategoryID].find(activity => activity.ActivityID === changedActivityAssignment.ActivityID);
    this.setActivitySummary(activityIndex);
    changedActivityAssignment.MinTime = selectedActivity.MinTime;
    changedActivityAssignment.MaxTime = selectedActivity.MaxTime;
    this.calculateTotalTimes();
    
    this.loadUnitsForActivity(changedActivityAssignment.ActivityID);

    if (selectedActivity.ActivityID.indexOf('~') > -1) {
      let unit = selectedActivity.ActivityID.split('~')[1];
      changedActivityAssignment.UnitNumber = +unit;
      this.unitChanged(activityIndex);
    }
  }

  setActivitySummary(activityIndex: number) {
    const summarizedActivity = this.editableAssignedActivities[activityIndex];
    if (!summarizedActivity || !summarizedActivity.CategoryID || !summarizedActivity.ActivityID) return;
    const selectedActivity = this.activityLists[summarizedActivity.CategoryID]
      .find(activity => activity.ActivityID === summarizedActivity.ActivityID);
    const selectedUnit = summarizedActivity.UnitNumber && this.unitLists[summarizedActivity.ActivityID]
      .find(unit => unit.Number === summarizedActivity.UnitNumber);
    this.activitySummary = 
    { 
      ImageName: (selectedUnit && selectedUnit.ImageName) || selectedActivity.ImageName, 
      Summary: selectedActivity.Summary,
      SummarizedActivity: summarizedActivity
    };
  }

  clearActivitySummary(assignedActivity: AssignedActivityDetail) {
    if(!assignedActivity || (this.activitySummary && assignedActivity === this.activitySummary.SummarizedActivity)) {
      this.activitySummary = null;
    }
  }

  unitChanged(activityIndex: number) {
    this.clearUnitError(activityIndex);
    this.changed = true;
    const changedActivityAssignment = this.editableAssignedActivities[activityIndex];
    if (!changedActivityAssignment.UnitNumber) return;

    this.ensureEmptyRow();
    this.setActivitySummary(activityIndex);
  }

  ensureEmptyRow() {
    const activitiesState = this.allAssignedActivitiesReady();
    if (activitiesState === -1) return;
    if (activitiesState === 1) {
      let activitiesCount = this.uneditableAssignedActivities.length + this.editableAssignedActivities.length;
      if (activitiesCount < 9)
        this.editableAssignedActivities.push(new AssignedActivityDetail());
    }
  }

  loadActivitiesForCategory(categoryId: number) {
    if (this.activityLists[categoryId] && this.activityLists[categoryId].length > 0) return;

    this.loading.activityLists[categoryId] = true;
    this.dataService.getActivitiesForCategory(categoryId).subscribe(activities => {
      let validActivities = activities.filter(a => a.Level != 8);
      this.activityLists[categoryId] = validActivities.sort((a, b) => {
        if (a.Level > b.Level) return 1;
        if (a.Level < b.Level) return -1;

        // Ensure that activities with a category but no Order are sorted to the top
        // Should help with diagnosing possible future issues
        if (!a.Order) return -1;
        if (!b.Order) return 1;

        if (a.Order > b.Order) return 1;
        if (a.Order < b.Order) return -1;
        
        return 0;
      });

      if (categoryId == environment.CONVERSATION_CATEGORY_ID) {
        this.activityLists[categoryId].push({
          ActivityID: this.kadyChainID + '~' + 1,
          Level: 10,
          Lesson: 1,
          Sequence: 1,
          SubSequence: 1,
          Title: "Kady Chain",
          Description: "3 min chat",
          MinTime: 3,
          MaxTime: 3,
          ImageName: "SS_KadyChain",
          Summary: "Chat with Kady",
          Order: 10
        },
        {
          ActivityID: this.kadyChainID + '~' + 2,
          Level: 10,
          Lesson: 1,
          Sequence: 1,
          SubSequence: 1,
          Title: "Kady Chain",
          Description: "5 min chat",
          MinTime: 5,
          MaxTime: 5,
          ImageName: "SS_KadyChain",
          Summary: "Chat with Kady",
          Order: 11
        },
        {
          ActivityID: this.kadyChainID + '~' + 3,
          Level: 10,
          Lesson: 1,
          Sequence: 1,
          SubSequence: 1,
          Title: "Kady Chain",
          Description: "10 min chat",
          MinTime: 10,
          MaxTime: 10,
          ImageName: "SS_KadyChain",
          Summary: "Chat with Kady",
          Order: 12
        });
      }
      this.loading.activityLists[categoryId] = false;
    });
  }

  loadUnitsForActivity(activityId: string) {
    if (this.unitLists[activityId]) return;

    if (activityId.indexOf('~') > -1) {
      let unit = activityId.split('~')[1];
      if (unit == '1') {
        this.unitLists[activityId] = [
          {
            ActivityID: this.kadyChainID,
            Number: 1,
            Description: "3 minutes (L1)"
          }
        ];
      }
      else if (unit == '2') {
        this.unitLists[activityId] = [
          {
            ActivityID: this.kadyChainID,
            Number: 2,
            Description: "5 minutes (L2)"
          }
        ];
      }
      else {
        this.unitLists[activityId] = [
          {
            ActivityID: this.kadyChainID,
            Number: 3,
            Description: "10 minutes (L3)"
          }
        ];
      }
      return;
    }

    this.loading.unitLists[activityId] = true;
    this.dataService.getUnitsForActivity(activityId).subscribe(units => {
      this.unitLists[activityId] = units;
      this.loading.unitLists[activityId] = false;
    });
  }

  // 0: some activities partially filled
  // 1: all activities completely filled
  // -1: no activities partially filled, some activities completely empty
  allAssignedActivitiesReady(): number {
    if (this.editableAssignedActivities.length === 0) return 1;

    const notCompleteActivities = this.editableAssignedActivities.filter(activity => {
      return !(activity.CategoryID && activity.ActivityID && activity.UnitNumber)
    });

    if (notCompleteActivities.length === 0) return 1;

    const notReadyActivities = notCompleteActivities.filter(activity => {
      return activity.CategoryID || activity.ActivityID || activity.UnitNumber;
    })

    if (notReadyActivities.length === 0) return -1;

    return 0;
  }

  activityLimitReached(): boolean {
    if (this.assignmentState == "completed") return false;

    let activitiesState = this.allAssignedActivitiesReady();

    if (activitiesState == 1) {
      let uneditableCount = this.uneditableAssignedActivities ? this.uneditableAssignedActivities.length : 0;
      let editableCount = this.editableAssignedActivities ? this.editableAssignedActivities.length : 0;
      let totalCount = uneditableCount + editableCount;
      if (totalCount >= 9) return true;
    }

    return false;
  }

  activityMoved(event: CdkDragDrop<AssignedActivityDetail>) {
    this.changed = true;
    moveItemInArray(this.editableAssignedActivities, event.previousIndex, event.currentIndex);
    this.moveValidations(event.previousIndex, event.currentIndex);
  }

  getCategoryTextForActivity(activity: AssignedActivityDetail): string {
    if (!activity.CategoryID) return "Select category";
    return this.activityCategories.find(category => category.ID === activity.CategoryID).Description;
  }

  getActivityTextForActivity(activity: AssignedActivityDetail): string {
    if (!activity.ActivityID || !activity.CategoryID) return "Select activity";
    if (this.isKadyChainID(activity.ActivityID)) return "KC - Kady Chain (Chat with Kady)"
    const activityDetail = this.activityLists[activity.CategoryID].find(activityDetails => activityDetails.ActivityID === activity.ActivityID);
    
    let levelString = `L${activityDetail.Level}`;
    if (activityDetail.Level == 8) levelString = "OB";
    if (activityDetail.Level == 9) levelString = "DC";
    if (activityDetail.Level == 10) levelString = "KC";

    return `${levelString} - ${activityDetail.Title} (${activityDetail.Description})`;
  }

  getUnitTextForActivity(activity: AssignedActivityDetail): string {
    if (!activity.CategoryID || !activity.ActivityID || !activity.UnitNumber) return "Select unit";
    const unitDetail = this.unitLists[activity.ActivityID].find(unit => unit.Number === activity.UnitNumber);
    return `${unitDetail.Number}. ${unitDetail.Description}`;
  }

  tryGoBack() {
    if (this.changed) {
      swal.fire({
        title: 'Are you sure?',
        text: 'This discards all of your changes to this assignment',
        type: 'warning',
        showConfirmButton: true,
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Discard Changes",
        customClass: {
          confirmButton: 'swal-button--danger',
        }
      }).then(res => {
        if (res.value) {
          this.goBack();
        }
      })
    }
    else {
      this.goBack();
    }
  }

  save() {
    this.saving = true;
    const valid = this.allAssignedActivitiesReady();
    if (valid === 0) {
      this.populateValidator();
      this.saving = false;
      return;
    }

    const savingCopy: AssignmentDetails = JSON.parse(JSON.stringify(this.assignment));
    savingCopy.ActivitySequence.Activities = 
    [ 
      ...(this.uneditableAssignedActivities || []), 
      ...(this.editableAssignedActivities).filter(activity => activity.CategoryID && activity.ActivityID && activity.UnitNumber)
    ];
    savingCopy.ActivitySequence.Activities.forEach(activity => {
      activity.ActivityID = activity.ActivityID.split('~')[0];
    })
    this.dataService.upsertClassAssignment(savingCopy).subscribe({
      next: (result) => {
        if (result) {
          Swal.fire({            
            title: "Saved!",
            text: "The assignment has been updated",
            type: "success",
            showConfirmButton: false,
            timer: 1000
          }).then(() => {
            this.goBack();
          });
        }
        else {
          this.saving = false;
        }
      },
      error: (err) => {
        this.saving = false;
        swal.fire('Oops!', err);
      }
    })
  }

  populateValidator() {
    this.editableAssignedActivities.forEach((activity, index) => {
      if (activity.CategoryID || activity.ActivityID || activity.UnitNumber) {
        this.validator[index] = {
          category: !activity.CategoryID,
          activity: !activity.ActivityID,
          unit: !activity.UnitNumber
        }
      }
      else {
        this.validator[index] = { category: false, activity: false, unit: false };
      }
    });
  }

  moveValidations(previousIndex, currentIndex) {
    if (!this.validator || this.validator.length === 0) return;

    let maxIndex = Math.max(previousIndex, currentIndex);
    for (let i = 0; i <= maxIndex; i++) {
      if (!this.validator[i]) this.validator[i] = { category: false, activity: false, unit: false };
    }

    moveItemInArray(this.validator, previousIndex, currentIndex);
  }

  isCategoryInvalid(activityIndex: number) {
    return this.validator && this.validator[activityIndex] && this.validator[activityIndex].category;
  }

  clearCategoryError(activityIndex: number) {
    if (this.validator && this.validator[activityIndex] && this.validator[activityIndex].category) {
      this.validator[activityIndex].category = false;
    }
  }

  isActivityInvalid(activityIndex: number) {
    return this.validator && this.validator[activityIndex] && this.validator[activityIndex].activity;
  }

  clearActivityError(activityIndex: number) {
    if (this.validator && this.validator[activityIndex] && this.validator[activityIndex].activity) {
      this.validator[activityIndex].activity = false;
    }
  }

  clearActivityRow(activityIndex: number) {
    const targetActivity = this.editableAssignedActivities[activityIndex];
    this.clearActivitySummary(targetActivity);
    this.editableAssignedActivities.splice(activityIndex, 1);
    if (this.validator && this.validator[activityIndex]) this.validator.splice(activityIndex, 1);
    this.ensureEmptyRow();
    this.changed = true;
  }

  clearActivityRowTime(activityIndex: number) {
    const targetActivity = this.editableAssignedActivities[activityIndex];
    targetActivity.MinTime = undefined;
    targetActivity.MaxTime = undefined;
    this.calculateTotalTimes();
  }

  isUnitInvalid(activityIndex: number) {
    return this.validator && this.validator[activityIndex] && this.validator[activityIndex].unit;
  }

  clearUnitError(activityIndex: number) {
    if (this.validator && this.validator[activityIndex] && this.validator[activityIndex].unit) {
      this.validator[activityIndex].unit = false;
    }
  }

  isAnyError() {
    return this.validator && this.validator.length > 0 && this.validator.some(activity => activity.category || activity.activity || activity.unit);
  }

  editHeader() {
    this.oldHeaderValues = 
    { 
      Name: this.assignment.ActivitySequence.Name, 
      StartDate: this.assignment.StartDate,
      EndDate: this.assignment.EndDate
    };

    this.editingHeader = true;
  }

  saveEditHeader() {
    this.editingHeader = false;
    this.oldHeaderValues = undefined;
  }

  cancelEditHeader() {
    this.assignment.ActivitySequence.Name = this.oldHeaderValues.Name;
    this.assignment.StartDate = this.oldHeaderValues.StartDate;
    this.assignment.EndDate = this.oldHeaderValues.EndDate;
    this.oldHeaderValues = undefined;
    this.editingHeader = false;
  }

  getAssignedStudentsText(): string {
    let assignedStudentsCount = this.assignment.AssignedStudents.length;

    if (assignedStudentsCount == 1) return "1 Student";
    if (assignedStudentsCount > 1) return `${assignedStudentsCount} Students`;
    return "All Students";
  }

  fixKadyChain(): void {
    let KadyChainActivities = this.assignment.ActivitySequence.Activities.filter(a => this.isKadyChainID(a.ActivityID));

    if (KadyChainActivities && KadyChainActivities.length) {
      KadyChainActivities.forEach(activity => {
        activity.CategoryID = environment.CONVERSATION_CATEGORY_ID;
        activity.Level = 10;
        activity.MaxTime = 10;
        activity.MinTime = 3;
        activity.Title = "Kady Chain";
        activity.ActivityID = activity.ActivityID + '~' + activity.UnitNumber;
      });
    }
  }

  kadyChainPrompt(activity: AssignedActivityDetail): boolean {
    return this.isKadyChainID(activity.ActivityID);
  }

  isKadyChainID(id: string): boolean {
    if (id == null) return false;
    return id.toUpperCase().includes(this.kadyChainID.toUpperCase());
  }
}
